import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Translation } from '@ps/web-shared-components';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardLoading from '@swf/core/hooks/useWizardLoading';
import { select } from '@swf/core/store';
import AccountVerification from '@swf/verification/AccountVerification/AccountVerification';

import useEkycTransitionScreenCheck from '../../../../hooks/useEkycTransitionScreenCheck';
import Container from '../../../Container/Container';
import FirstLoader from '../../../FirstLoader/FirstLoader';
import HelpModal from '../../../HelpModal/HelpModal';
import useRmfLogout from '../../hooks/useRmfLogout';
import RMFEKYCTransitionScreen from '../RMFEKYCTransitionScreen/RMFEKYCTransitionScreen';

import {
  TXTCLI_LetXs_get_you_verified,
  TXTWCL_WeXre_working_on_this,
  TXTWCL_We_need_to_verify_your_identit_ELL,
  TXTWCL_We_need_to_verify_your_identit_ELL_2
} from './RMFVerification.messages.json';
import Celebration from './components/Celebration/Celebration';
import ProceedToDeposit from './components/ProceedToDeposit/ProceedToDeposit';
import Verification from './components/Verification/Verification';

const RMFVerification: React.FC = () => {
  const exitWizard = useWizardExit();
  const site = useSelector(select.cms.getSiteId);
  const isFrance = site === Sites.FRA;
  const isUK = site === Sites.UK;
  const isCAON = site === Sites.CA_ON;
  const [panelVisible, setPanelVisible] = useState(false);
  const [showScreenToDeposit, setShowScreenToDeposit] = useState(false);
  const isStandaloneRmf = useSelector(select.location.getPathname).includes('/real-money-form');
  const shouldShowEKYCTransition = useEkycTransitionScreenCheck();
  const [verificationsVisible, setVerificationsVisible] = useState(!isFrance);
  const logout = useRmfLogout();
  const { loading } = useWizardLoading();
  const isEmbedded = useSelector(select.device.isEmbedded);

  const handleKYCModalButtons = () => {
    if (isEmbedded) {
      window.starsweb?.app?.execute('docverify');
      return;
    }
    setVerificationsVisible(true);
  };

  const handleClose = () => {
    if (isUK || isCAON) {
      setPanelVisible(false);
    } else {
      !isStandaloneRmf && exitWizard();
    }
  };

  const handleOnComplete = () => {
    if (isFrance && !isStandaloneRmf) {
      setShowScreenToDeposit(true);
    } else {
      exitWizard();
      logout?.();
    }
  };

  return (
    <Container
      page={RMFVerification}
      headerProps={{
        title: !loading && <Translation {...TXTCLI_LetXs_get_you_verified} />,
        buttons: { back: !(isUK || isCAON) },
        hideProgressBar: true
      }}
      {...(!loading &&
        verificationsVisible && {
          pageTopTitle: <Translation {...TXTCLI_LetXs_get_you_verified} />,
          pageTopSubtitle: (
            <Translation
              {...(isUK
                ? TXTWCL_We_need_to_verify_your_identit_ELL_2
                : TXTWCL_We_need_to_verify_your_identit_ELL)}
            />
          )
        })}
    >
      {!loading && (
        <>
          {shouldShowEKYCTransition && (
            <RMFEKYCTransitionScreen
              showVerifications={handleKYCModalButtons}
              transitionType="documentVerification"
            />
          )}
          {!shouldShowEKYCTransition && <Celebration />}
          {verificationsVisible && (
            <>
              <Verification handleClick={() => setPanelVisible(true)} />
              <HelpModal
                visible={panelVisible}
                onClose={() => setPanelVisible(false)}
                title={TXTCLI_LetXs_get_you_verified}
                hideProgressBar
              >
                <AccountVerification
                  openIDScan
                  showButton
                  onComplete={handleOnComplete}
                  onClose={handleClose}
                  handleCancel={() => setPanelVisible(false)}
                />
              </HelpModal>
            </>
          )}
          {isFrance && showScreenToDeposit && <ProceedToDeposit />}
        </>
      )}
      {loading && <FirstLoader label={TXTWCL_WeXre_working_on_this} />}
    </Container>
  );
};

RMFVerification.displayName = 'RMFVerification';

export default RMFVerification;
